import {
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Input,
  Select as ChakraSelect,
  Stack,
  Text,
  Textarea,
  VStack,
} from '@chakra-ui/react';
import { Select as AntdSelect } from 'antd';
import React from 'react';
import COUNTRY_OPTIONS from '../../../data/countries';
import BrandButton from '../../../components/brandButton/BrandButton';

const { Option } = AntdSelect;

const TYPE_OPTIONS = ['VC', 'Angel', 'Accelerator', 'DFI', 'Grant'];
const GEOGRAPHY_OPTIONS = [
  'Pan-Africa',
  'West Africa',
  'East Africa',
  'Southern Africa',
  'North Africa',
  'Central Africa',
];
const STAGE_OPTIONS = ['Pre-seed', 'Seed', 'Series A', 'Growth'];
const SECTOR_OPTIONS = [
  'Fintech',
  'Health',
  'Education',
  'Agritech',
  'Energy',
  'Logistics',
  'Retail',
  'Consumer',
  'SaaS',
];

const FunderForm = ({
  values,
  errors,
  warnings,
  isLoading,
  readOnly,
  onChange,
  onMultiChange,
  onSubmit,
  onCancel,
  submitLabel,
  surveydata,
}) => {
  return (
    <Box bg="white" p={6} borderRadius="16px" boxShadow="sm">
      <Stack spacing={6}>
        <Text fontSize="2xl" fontWeight={700}>
          {submitLabel}
        </Text>

        <VStack spacing={4} alignItems="stretch">
          <FormControl isRequired isInvalid={!!errors.name}>
            <FormLabel>Funder Name</FormLabel>
            <Input
              name="name"
              value={values.name}
              onChange={onChange}
              placeholder="Enter funder name"
              isDisabled={readOnly}
            />
            <FormErrorMessage>{errors.name}</FormErrorMessage>
          </FormControl>

          {warnings?.name && (
            <Text color="orange.600" fontSize="sm">
              {warnings.name}
            </Text>
          )}

          <HStack
            spacing={4}
            flexWrap="wrap"
            className="item-start"
            alignItems="flex-start"
          >
            <FormControl flex={1} isRequired isInvalid={!!errors.type}>
              <FormLabel>Type</FormLabel>
              <ChakraSelect
                name="type"
                value={values.type}
                onChange={onChange}
                placeholder="Select type"
                isDisabled={readOnly}
              >
                {TYPE_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </ChakraSelect>
              <FormErrorMessage>{errors.type}</FormErrorMessage>
            </FormControl>

            <FormControl flex={1} isRequired isInvalid={!!errors.hq_country}>
              <FormLabel>HQ Country</FormLabel>
              <ChakraSelect
                name="hq_country"
                value={values.hq_country}
                onChange={onChange}
                placeholder="Select country"
                isDisabled={readOnly}
              >
                {COUNTRY_OPTIONS.map((country) => (
                  <option key={country} value={country}>
                    {country}
                  </option>
                ))}
              </ChakraSelect>
              <FormErrorMessage>{errors.hq_country}</FormErrorMessage>
            </FormControl>
          </HStack>

          <HStack
            spacing={4}
            flexWrap="wrap"
            className="item-start"
            alignItems="flex-start"
          >
            <FormControl flex={1} isInvalid={!!errors.geography_focus}>
              <FormLabel>Geography Focus</FormLabel>
              <AntdSelect
                mode="multiple"
                placeholder="Choose geography focus"
                value={values.geography_focus}
                onChange={(value) => onMultiChange('geography_focus', value)}
                style={{ width: '100%' }}
                disabled={readOnly}
              >
                {surveydata.locations.map((option, index) => (
                  <Option key={index} value={option.name}>
                    {option.name}
                  </Option>
                ))}
              </AntdSelect>
              <FormErrorMessage>{errors.geography_focus}</FormErrorMessage>
            </FormControl>

            <FormControl flex={1} isInvalid={!!errors.stage_focus}>
              <FormLabel>Stage Focus</FormLabel>
              <AntdSelect
                mode="multiple"
                placeholder="Choose stage focus"
                value={values.stage_focus}
                onChange={(value) => onMultiChange('stage_focus', value)}
                style={{ width: '100%' }}
                disabled={readOnly}
              >
                {STAGE_OPTIONS.map((option) => (
                  <Option key={option} value={option}>
                    {option}
                  </Option>
                ))}
              </AntdSelect>
              <FormErrorMessage>{errors.stage_focus}</FormErrorMessage>
            </FormControl>
          </HStack>

          <FormControl isInvalid={!!errors.sector_focus}>
            <FormLabel>Sector Focus</FormLabel>
            <AntdSelect
              mode="tags"
              placeholder="Add sector tags"
              value={values.sector_focus}
              onChange={(value) => onMultiChange('sector_focus', value)}
              style={{ width: '100%' }}
              disabled={readOnly}
            >
              {surveydata.categories.map((option, index) => (
                <Option key={index} value={option.title}>
                  {option.title}
                </Option>
              ))}
            </AntdSelect>
            <FormErrorMessage>{errors.sector_focus}</FormErrorMessage>
          </FormControl>

          <HStack
            spacing={4}
            flexWrap="wrap"
            className="item-start"
            alignItems="flex-start"
          >
            <FormControl flex={1} isInvalid={!!errors.website_url}>
              <FormLabel>Website URL</FormLabel>
              <Input
                name="website_url"
                value={values.website_url}
                onChange={onChange}
                placeholder="https://example.com"
                isDisabled={readOnly}
              />
              <FormErrorMessage>{errors.website_url}</FormErrorMessage>
            </FormControl>

            <FormControl flex={1} isInvalid={!!errors.application_url}>
              <FormLabel>Application URL</FormLabel>
              <Input
                name="application_url"
                value={values.application_url}
                onChange={onChange}
                placeholder="https://application.link"
                isDisabled={readOnly}
              />
              <FormErrorMessage>{errors.application_url}</FormErrorMessage>
            </FormControl>
          </HStack>

          <HStack
            spacing={4}
            flexWrap="wrap"
            className="item-start"
            alignItems="flex-start"
          >
            <FormControl flex={1} isInvalid={!!errors.contact_name}>
              <FormLabel>Contact Name</FormLabel>
              <Input
                name="contact_name"
                value={values.contact_name}
                onChange={onChange}
                placeholder="Contact person"
                isDisabled={readOnly}
              />
              <FormErrorMessage>{errors.contact_name}</FormErrorMessage>
            </FormControl>

            <FormControl flex={1} isInvalid={!!errors.contact_email}>
              <FormLabel>Contact Email</FormLabel>
              <Input
                name="contact_email"
                value={values.contact_email}
                onChange={onChange}
                placeholder="email@example.com"
                isDisabled={readOnly}
              />
              <FormErrorMessage>{errors.contact_email}</FormErrorMessage>
            </FormControl>
          </HStack>

          <HStack
            spacing={4}
            flexWrap="wrap"
            className="item-start"
            alignItems="flex-start"
          >
            <FormControl flex={1} isInvalid={!!errors.cheque_min}>
              <FormLabel>Typical Cheque Min</FormLabel>
              <Input
                name="cheque_min"
                value={values.cheque_min}
                onChange={onChange}
                placeholder="Minimum cheque"
                type="number"
                isDisabled={readOnly}
              />
              <FormErrorMessage>{errors.cheque_min}</FormErrorMessage>
            </FormControl>

            <FormControl flex={1} isInvalid={!!errors.cheque_max}>
              <FormLabel>Typical Cheque Max</FormLabel>
              <Input
                name="cheque_max"
                value={values.cheque_max}
                onChange={onChange}
                placeholder="Maximum cheque"
                type="number"
                isDisabled={readOnly}
              />
              <FormErrorMessage>{errors.cheque_max}</FormErrorMessage>
            </FormControl>
          </HStack>

          <FormControl isInvalid={!!errors.description}>
            <FormLabel>Description / Investment Thesis</FormLabel>
            <Textarea
              name="description"
              value={values.description}
              onChange={onChange}
              placeholder="Describe the funder's investment focus"
              rows={4}
              isDisabled={readOnly}
            />
            <FormErrorMessage>{errors.description}</FormErrorMessage>
          </FormControl>

          <FormControl>
            <FormLabel>Portfolio Examples</FormLabel>
            <AntdSelect
              mode="tags"
              placeholder="Add portfolio examples"
              value={values.portfolio_examples}
              onChange={(value) => onMultiChange('portfolio_examples', value)}
              style={{ width: '100%' }}
              disabled={readOnly}
            />
          </FormControl>

          <FormControl>
            <FormLabel>Notes (internal only)</FormLabel>
            <Textarea
              name="notes"
              value={values.notes}
              onChange={onChange}
              placeholder="Internal notes for the admin team"
              rows={3}
              isDisabled={readOnly}
            />
          </FormControl>

          <FormControl>
            <FormLabel>Status</FormLabel>
            <ChakraSelect
              name="status"
              value={values.status}
              onChange={onChange}
              placeholder="Select status"
              isDisabled={readOnly}
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </ChakraSelect>
          </FormControl>
        </VStack>

        <Flex className="justify-end w-full" gap={3} pt={4} flexWrap="wrap">
          <BrandButton
            text="Cancel"
            variant="outline"
            onClick={onCancel}
            isDisabled={isLoading}
          />

          <BrandButton
            fontSize="13px"
            text="Create"
            borderColor="#C7DFF0"
            bg={'#134A70'}
            colorScheme="blue"
            w="full"
            maxW="150px"
            onClick={onSubmit}
            isLoading={isLoading}
            isDisabled={isLoading}
          />
        </Flex>
      </Stack>
    </Box>
  );
};

export default FunderForm;
